// WYSIWYG edit-mode renderer for a canvas's rows. Renders the same grid
// markup as CanvasItems (shared rowGridColumns) so edit mode looks exactly
// like view mode, plus edit affordances: a per-cell drag handle (dnd-kit),
// width dividers between cells, an inline caption input, and remove.
//
// Controlled: every mutation flows up through onChange(nextRows). The only
// local state is transient — the in-flight drag id and the divider preview —
// so Cancel in the host page discards everything by dropping the draft.
//
// Drop targets come in two kinds, mirroring the rows model: a row gap
// (before/after/between rows → the cell gets its own full-width row) and a
// cell gap (inside a row → the cell joins that row). Solo-kind cells
// (SOLO_ROW_KINDS — treemap) only see row-gap targets; moveCell also rejects
// the illegal drop as a backstop, and the input rows are canonicalized
// through enforceSoloRows (mirroring CanvasItems), so a stored violation —
// a treemap already sharing a row — renders split and every edit re-emits
// the enforced shape.

import { useCallback, useMemo, useRef, useState } from "react";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import KernelWidget from "./KernelWidget.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { resolveCanvasCellKernel } from "../lib/kernelResolver.js";
import {
    SOLO_ROW_KINDS,
    applyDividerDrag,
    enforceSoloRows,
    moveCell,
    normalizeSingletonWidths,
    resetRowWidths,
    rowFractions,
    rowGridColumns,
} from "../lib/canvasLayout.js";

const GAP_PX = 10;

function cellIdentity(cell) {
    return cell?.kernel_id || cell?.command;
}

// Resolve a dnd-kit drag-end event against the current rows: returns the next
// rows array, or null when nothing should change (dropped outside any target,
// a droppable without target data, or a move moveCell rejects/no-ops via its
// same-reference contract). Exported for direct unit testing — jsdom can't
// exercise dnd-kit's pointer hit-testing.
export function resolveDragEnd(rows, kindOf, { active, over }) {
    const target = over?.data?.current?.target;
    if (!target) return null;
    const next = moveCell(rows, active.id, target, kindOf);
    return next === rows ? null : next;
}

const inputCls =
    "min-h-[26px] w-full rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[11px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)]";

export default function CanvasRowsEditor({
    rows,
    byCommand,
    kernelResolver,
    legacyCellAdapter,
    runContext,
    version = 0,
    onChange,
}) {
    const resolveCell = useCallback(
        (cell) => (kernelResolver
            ? resolveCanvasCellKernel(cell, kernelResolver, {
                legacyAdapter: legacyCellAdapter,
            })
            : { body: byCommand?.get(cell.command) }),
        [byCommand, kernelResolver, legacyCellAdapter],
    );
    const kindOf = useCallback(
        (command, cell = { command }) => resolveCell(cell)?.body?.render?.kind,
        [resolveCell],
    );
    // Canonicalize the input: a stored violation of the solo-row rule (e.g. a
    // treemap sharing a row, authorable via the legacy flat editor) renders
    // split, and every edit below bases — and re-emits — the enforced shape.
    const canonicalRows = useMemo(() => enforceSoloRows(rows, kindOf), [rows, kindOf]);
    const [activeId, setActiveId] = useState(null);
    const activeCell = useMemo(
        () => canonicalRows
            .flatMap((row) => row.cells)
            .find((cell) => cellIdentity(cell) === activeId),
        [activeId, canonicalRows],
    );
    const draggingSolo = activeCell != null
        && SOLO_ROW_KINDS.has(kindOf(activeCell.command, activeCell));
    // Divider drag preview: rows with one row substituted, shown until commit.
    const [preview, setPreview] = useState(null); // { rowIndex, row } | null

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor),
    );

    const handleDragEnd = (event) => {
        setActiveId(null);
        const next = resolveDragEnd(canonicalRows, kindOf, event);
        if (next) onChange(next);
    };

    const displayRows = preview
        ? canonicalRows.map((row, ri) => (ri === preview.rowIndex ? preview.row : row))
        : canonicalRows;

    const updateCaption = (identity, caption) => {
        onChange(canonicalRows.map((row) => ({
            cells: row.cells.map((cell) => {
                if (cellIdentity(cell) !== identity) return cell;
                const next = { ...cell };
                if (caption.trim()) next.caption = caption;
                else delete next.caption;
                return next;
            }),
        })));
    };
    const removeCell = (identity) => {
        onChange(normalizeSingletonWidths(canonicalRows
            .map((row) => ({
                cells: row.cells.filter((cell) => cellIdentity(cell) !== identity),
            }))
            .filter((row) => row.cells.length > 0)));
    };
    const commitRow = (rowIndex, row) => {
        setPreview(null);
        if (row === canonicalRows[rowIndex]) return;
        onChange(canonicalRows.map((r, ri) => (ri === rowIndex ? row : r)));
    };

    return (
        <DndContext
            sensors={sensors}
            onDragStart={({ active }) => setActiveId(active.id)}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
        >
            <div className="flex flex-col min-w-0 [overflow-wrap:anywhere]">
                <RowGap index={0} active={activeId != null} />
                {displayRows.map((row, ri) => (
                    <div key={ri} className="min-w-0">
                        <EditRow
                            row={row}
                            rowIndex={ri}
                            resolveCell={resolveCell}
                            runContext={runContext}
                            version={version}
                            dragging={activeId != null}
                            draggingSolo={draggingSolo}
                            kindOf={kindOf}
                            onCaption={updateCaption}
                            onRemove={removeCell}
                            onPreviewRow={(r) => setPreview({ rowIndex: ri, row: r })}
                            onCommitRow={(r) => commitRow(ri, r)}
                        />
                        <RowGap index={ri + 1} active={activeId != null} />
                    </div>
                ))}
            </div>
            <DragOverlay dropAnimation={null}>
                {activeCell ? (
                    <span className="inline-block rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[3px] font-mono text-[11px] text-[color:var(--ink)] shadow-sm">
                        /{activeCell.command}
                    </span>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

// Horizontal drop bar between rows (and before the first / after the last).
// Always mounted so dnd-kit can hit-test it; visually present only during a
// drag, highlighted when hovered.
function RowGap({ index, active }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `row-gap-${index}`,
        data: { target: { type: "row", index } },
    });
    return (
        <div
            ref={setNodeRef}
            className={`transition-[height,background-color] duration-100 ${
                active
                    ? `h-[14px] rounded-[2px] ${isOver ? "bg-[color:var(--green-soft)] outline outline-1 outline-[color:var(--green)]" : "bg-[color:var(--line-soft)]"}`
                    : "h-[10px]"
            }`}
            aria-hidden="true"
        />
    );
}

function EditRow({
    row, rowIndex, resolveCell, runContext, version, dragging, draggingSolo, kindOf,
    onCaption, onRemove, onPreviewRow, onCommitRow,
}) {
    const rowRef = useRef(null);
    const hasSolo = row.cells.some(
        (cell) => SOLO_ROW_KINDS.has(kindOf(cell.command, cell)),
    );
    // Cell-gap drops are only legal when neither side of the drop is solo-kind.
    const cellDropsEnabled = dragging && !draggingSolo && !hasSolo;
    const fractions = rowFractions(row);
    const gaps = (row.cells.length - 1) * GAP_PX;
    return (
        <div
            ref={rowRef}
            className="relative grid gap-[10px] min-w-0"
            style={{ gridTemplateColumns: rowGridColumns(row) }}
        >
            {row.cells.map((cell) => (
                <EditCell
                    key={cellIdentity(cell)}
                    cell={cell}
                    resolvedDefinition={resolveCell(cell)}
                    runContext={runContext}
                    version={version}
                    onCaption={onCaption}
                    onRemove={onRemove}
                />
            ))}
            {cellDropsEnabled
                ? Array.from({ length: row.cells.length + 1 }, (_, i) => (
                    <CellGap key={i} rowIndex={rowIndex} index={i} fractions={fractions} gaps={gaps} />
                ))
                : null}
            {!dragging
                ? fractions.slice(0, -1).map((_, boundary) => (
                    <Divider
                        key={boundary}
                        boundary={boundary}
                        fractions={fractions}
                        gaps={gaps}
                        rowRef={rowRef}
                        row={row}
                        onPreviewRow={onPreviewRow}
                        onCommitRow={onCommitRow}
                    />
                ))
                : null}
        </div>
    );
}

function EditCell({
    cell,
    resolvedDefinition,
    runContext,
    version,
    onCaption,
    onRemove,
}) {
    const identity = cellIdentity(cell);
    const kernel = resolvedDefinition?.body;
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
        id: identity,
    });
    return (
        <article
            ref={setNodeRef}
            className={`group flex min-w-0 max-w-full flex-col overflow-hidden rounded-[2px] border bg-[color:var(--bg)] px-[10px] py-[10px] ${
                isDragging
                    ? "border-dashed border-[color:var(--line-strong)] opacity-40"
                    : "border-[color:var(--line)]"
            }`}
        >
            <header className="mb-[8px] flex items-center gap-[8px]">
                <button
                    type="button"
                    ref={setActivatorNodeRef}
                    {...attributes}
                    {...listeners}
                    className="shrink-0 cursor-grab touch-none bg-transparent text-[13px] leading-[1] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] active:cursor-grabbing"
                    aria-label={`Drag ${cell.command} to rearrange`}
                    title="Drag to rearrange. Drop between rows for a full-width row, or beside a card to share its row."
                >
                    ⠿
                </button>
                <span className="shrink-0 whitespace-nowrap font-mono text-[10px] text-[color:var(--muted-soft)]">
                    /{cell.command}
                </span>
                <input
                    type="text"
                    value={cell.caption || ""}
                    onChange={(e) => onCaption(identity, e.target.value)}
                    placeholder="caption"
                    className={inputCls}
                />
                <button
                    type="button"
                    onClick={() => onRemove(identity)}
                    className="shrink-0 bg-transparent text-[12px] leading-[1] text-[color:var(--muted-soft)] hover:text-[color:var(--red,#c0392b)] cursor-pointer"
                    aria-label={`Remove ${cell.command} from canvas`}
                >
                    ×
                </button>
            </header>
            {kernel ? (
                <KernelWidget
                    kernel={kernel}
                    resolvedDefinition={resolvedDefinition}
                    runContext={runContext}
                    version={version}
                    bare
                    fill={false}
                    showHeader={false}
                />
            ) : (
                <div className={NOTE_CLASS}>
                    Missing kernel: <code>/{cell.command}</code> is no longer available.
                </div>
            )}
        </article>
    );
}

// Vertical drop bar at a cell boundary inside a row. Absolutely positioned
// over the grid gap; fr tracks distribute (100% − gaps) by weight, so the
// boundary sits at the cumulative fraction of that span plus the gaps passed.
function cellGapLeft(fractions, gaps, index) {
    if (index === 0) return "0px";
    const cum = fractions.slice(0, index).reduce((a, b) => a + b, 0);
    return `calc((100% - ${gaps}px) * ${cum} + ${(index - 1) * GAP_PX}px)`;
}

function CellGap({ rowIndex, index, fractions, gaps }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `cell-gap-${rowIndex}-${index}`,
        data: { target: { type: "cell", row: rowIndex, index } },
    });
    // Boundary 0 sits at the row's left edge, boundary N at its right edge;
    // interior boundaries land in the grid gaps via cellGapLeft.
    const left = index === 0
        ? "0px"
        : index === fractions.length
            ? "100%"
            : cellGapLeft(fractions, gaps, index);
    return (
        <div className="pointer-events-none absolute inset-0" style={{ gridColumn: "1 / -1" }}>
            <div
                ref={setNodeRef}
                className={`pointer-events-auto absolute top-0 bottom-0 w-[14px] -translate-x-1/2 rounded-[2px] ${
                    isOver ? "bg-[color:var(--green-soft)] outline outline-1 outline-[color:var(--green)]" : "bg-[color:var(--line-soft)] opacity-60"
                }`}
                style={{ left }}
                aria-hidden="true"
            />
        </div>
    );
}

function Divider({ boundary, fractions, gaps, rowRef, row, onPreviewRow, onCommitRow }) {
    const drag = useRef(null); // { startX, baseRow } | null
    const latest = useRef(row);
    const onPointerDown = (e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = { startX: e.clientX, baseRow: row };
        latest.current = row;
    };
    const onPointerMove = (e) => {
        if (!drag.current) return;
        const width = rowRef.current?.getBoundingClientRect()?.width || 0;
        if (!width) return;
        const delta = (e.clientX - drag.current.startX) / width;
        const next = applyDividerDrag(drag.current.baseRow, boundary, delta);
        latest.current = next;
        onPreviewRow(next);
    };
    const onPointerUp = () => {
        if (!drag.current) return;
        drag.current = null;
        onCommitRow(latest.current);
    };
    const cum = fractions.slice(0, boundary + 1).reduce((a, b) => a + b, 0);
    return (
        <div className="pointer-events-none absolute inset-0" style={{ gridColumn: "1 / -1" }}>
            <div
                data-divider="true"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onDoubleClick={() => onCommitRow(resetRowWidths(row))}
                className="group/divider pointer-events-auto absolute top-0 bottom-0 flex w-[10px] cursor-col-resize items-center justify-center"
                style={{ left: `calc((100% - ${gaps}px) * ${cum} + ${boundary * GAP_PX}px)` }}
                title="Drag to resize. Double-click for equal widths."
                role="separator"
                aria-orientation="vertical"
            >
                <span className="h-[24px] w-[3px] rounded-full bg-[color:var(--line)] transition-colors group-hover/divider:bg-[color:var(--ink)]" aria-hidden="true" />
            </div>
        </div>
    );
}
